import Button from '@/src/components/atom/Button';
import DateRangePicker from '@/src/components/atom/DateRangePicker';
import Dialog from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import Table from '@/src/components/atom/Table';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { ChallengeParticipantListColumn } from '@/src/pages/Challenge/column';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { GetChallengeParticipantGroupByRequestParams } from '@/src/apis/rest/challenge.dtos';
import {
  getChallengeParticipantByGroupAPI,
  getChallengeParticipantExcelAPI,
} from '@/src/apis/rest/challenge';
import ParticipantDetailModal from '@/src/pages/Challenge/components/ParticipantDetailModal';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';

type ModalState = 'NONE' | 'DETAIL';

interface ChallengeParticipantsModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  challengeId: number | null;
  contentStartedAt?: string;
  contentEndedAt?: string;
}

interface FormValues
  extends Partial<
    GetChallengeParticipantGroupByRequestParams & { createdAt: DateRange }
  > {
  createdAt: {
    from: undefined;
    to: undefined;
  };
}

const DEFAULT_VALIES = {};

export default function ChallengeParticipantsModal({
  open: openProps,
  onOpenChange,
  challengeId,
  contentStartedAt,
  contentEndedAt,
}: ChallengeParticipantsModalProps) {
  // state
  const gridRef = useRef<AgGridReact>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalState, setModalState] = useState<ModalState>('NONE');
  const [filterValues, setFilterValues] = useState<
    Omit<FormValues, 'createdAt'>
  >({});
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );

  // form
  const form = useForm<FormValues>({
    defaultValues: DEFAULT_VALIES,
  });

  // query
  const {
    data: challengeParticipantData,
    refetch,
    isError,
  } = useQuery({
    queryKey: [
      'challenge-participants',
      'group-by',
      challengeId,
      page,
      pageSize,
    ],
    queryFn: () => {
      return getChallengeParticipantByGroupAPI({
        page,
        pageSize,
        challengeId: challengeId || 0,
        ...form.watch(),
      });
    },
    enabled: !!challengeId,
  });

  // event
  const handleOpenSubModal = (type: ModalState) => {
    setModalState(type);
  };
  const handleCloseSubModal = () => {
    setModalState('NONE');
  };
  const handleSubmit = (values: FormValues) => {
    setPage(1);

    const newValues = { ...values };

    setFilterValues(newValues);
  };

  const handleCloseModal = () => {
    onOpenChange(false);
  };
  const handlePageChange = (page: number, pageSize: number) => {
    setPage(page);
    setPageSize(pageSize);
  };

  // render
  const getDateDisabledMatcher = (date: Date) => {
    if (dayjs(contentStartedAt).isAfter(date, 'D')) return true;
    if (dayjs(contentEndedAt).isBefore(date, 'D')) return true;

    return false;
  };

  return (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[967px]',
        }}
        title={
          <div className="flex items-center gap-2">
            참여한 회원 목록
            <ExcelDownloadButton
              buttonSize="small"
              searchParams={filterValues}
              downloadExcelAPI={(password, searchParams) =>
                getChallengeParticipantExcelAPI(password, {
                  ...searchParams,
                  challengeId: Number(challengeId),
                })
              }
              fileName="챌린지_참여_회원_리스트"
            />
          </div>
        }
      >
        <Form {...form}>
          <form
            className="mb-4 grid grid-cols-[1fr_auto_1fr_auto] gap-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="createdAt"
              render={({ field, fieldState: { error } }) => (
                <FormContent
                  label="참여 기간"
                  labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                  error={error}
                >
                  <DateRangePicker
                    {...field}
                    onChange={e => {
                      field.onChange(e);

                      form.setValue(
                        'createdAtFrom',
                        e?.from ? dayjs(e?.from).format('YYYY-MM-DD') : '',
                      );
                      form.setValue(
                        'createdAtTo',
                        e?.to ? dayjs(e?.to).format('YYYY-MM-DD') : '',
                      );
                    }}
                    dateDisabledMatcher={getDateDisabledMatcher}
                    defaultMonth={
                      contentStartedAt ? new Date(contentStartedAt) : undefined
                    }
                  />
                </FormContent>
              )}
            />
            <div className="h-full w-[1px] bg-gray1" />
            <FormField
              control={form.control}
              name="nickLike"
              render={({ field, fieldState: { error } }) => (
                <FormContent
                  label="닉네임"
                  labelClassName="w-[144px] !text-b16m text-gray7 translate-y-2 font-pretendardMedium"
                  error={error}
                >
                  <Input
                    {...field}
                    onClear={() => field.onChange('')}
                    placeholder="닉네임을 입력해주세요"
                  />
                </FormContent>
              )}
            />
            <Button
              type="submit"
              className="flex-shrink-0"
              buttonColor="secondary"
              onClick={() => refetch()}
            >
              {BUTTON_CONST.search}
            </Button>
          </form>
        </Form>
        <Table
          ref={gridRef}
          rowData={isError ? [] : challengeParticipantData?.rows || []}
          isPaginationDataMapping
          columnDefs={ChallengeParticipantListColumn(customerId => {
            setSelectedCustomerId(customerId);
            handleOpenSubModal('DETAIL');
          })}
          total={challengeParticipantData?.count || 0}
          page={page}
          pageSize={pageSize}
          handlePaginationClick={handlePageChange}
        />
      </Dialog>
      {modalState === 'DETAIL' && (
        <ParticipantDetailModal
          open={modalState === 'DETAIL'}
          onOpenChange={handleCloseSubModal}
          challengeId={challengeId}
          customerId={selectedCustomerId}
        />
      )}
    </>
  );
}
